import is from '@sindresorhus/is';
import clsx from 'clsx';
import { Fragment, type JSX, type ReactNode } from 'react';

import { Button } from '../../../core/button/Button';
import { IconArrowDown, IconArrowUp, IconCrossSmall, IconExpand45, IconMinimize45 } from '../../../core/icons';
import { TileControlGroup } from '../../../core/tile/Tile';
import type { Language } from '../../language-picker/LanguagePicker';
import { LanguagePicker } from '../../language-picker/LanguagePicker';
import { useTileWidthToggle } from '../../tiling-engine/TilingEngineContext';

export const HeaderContainer: React.FunctionComponent<{
    readonly children: ReactNode;
    readonly className?: string;
}> = ({ children, className }) => (
    <div
        className={clsx(
            'flex items-center box-border flex-row shrink-0 justify-between p-0 relative w-full z-[1] font-sans',
            className,
        )}
    >
        {children}
    </div>
);

export const NavigationContainer: React.FunctionComponent<{
    readonly children: ReactNode;
    readonly className?: string;
}> = ({ children, className }) => (
    <div className={clsx('flex items-center self-stretch flex-row', className)}>{children}</div>
);

export const NavigationButtons: React.FunctionComponent<{
    readonly children: ReactNode;
    readonly className?: string;
}> = ({ children, className }) => (
    <div
        className={clsx(
            'flex items-center box-border flex-row shrink-0 flex-wrap h-full justify-start p-0 relative',
            className,
        )}
    >
        {children}
    </div>
);

export const ActionButtons: React.FunctionComponent<{
    readonly children: ReactNode;
    readonly className?: string;
}> = ({ children, className }) => (
    <div className={clsx('flex items-center box-border flex-row shrink-0 gap-m justify-start p-0 relative', className)}>
        {children}
    </div>
);

export const Seperator: React.FunctionComponent<{ readonly className?: string }> = ({ className }) => (
    <div className={clsx('bg-border-subtle h-[1rem] mx-xxl w-px', className)} />
);

type SidepanelHeaderProps<T extends string> = {
    readonly actions?: ReactNode;
    readonly activeLanguage: string | undefined;
    readonly disableArrows?: boolean;
    readonly isGenerating: boolean;
    readonly isLanguagePickerDisabled: boolean;
    readonly languages: readonly Language<T>[];
    readonly leadingAffordance?: ReactNode;
    readonly onClose?: () => void;
    readonly onNext?: () => void;
    readonly onPrevious?: () => void;
    readonly renderLanguageIndicator?: (language: Language<T>) => ReactNode;
    readonly setActiveLanguage: (value: T) => void;
};

export const SidepanelHeader = <T extends string>({
    actions,
    activeLanguage,
    disableArrows = false,
    isGenerating,
    isLanguagePickerDisabled,
    languages,
    leadingAffordance,
    onClose,
    onNext,
    onPrevious,
    setActiveLanguage,
}: SidepanelHeaderProps<T>): JSX.Element => {
    const widthToggle = useTileWidthToggle();
    const isMaximized = widthToggle?.isMaximized === true;

    return (
        <HeaderContainer>
            <NavigationContainer>
                <NavigationButtons>
                    <div className="flex items-center box-border flex-row shrink-0 gap-m justify-start p-0 relative">
                        {leadingAffordance}
                        {is.undefined(onClose) === false && (
                            <TileControlGroup>
                                <Button
                                    aria-label="close"
                                    iconOnly={true}
                                    size="sm"
                                    variant="secondary"
                                    onClick={onClose}
                                >
                                    <IconCrossSmall />
                                </Button>
                                <Button
                                    aria-label={isMaximized ? 'collapse' : 'expand'}
                                    disabled={is.null(widthToggle)}
                                    iconOnly={true}
                                    size="sm"
                                    variant="secondary"
                                    onClick={widthToggle?.toggle}
                                >
                                    {isMaximized ? <IconMinimize45 /> : <IconExpand45 />}
                                </Button>
                            </TileControlGroup>
                        )}
                    </div>
                    {(is.undefined(leadingAffordance) === false || is.undefined(onClose) === false) && <Seperator />}
                    {!disableArrows && (
                        <Fragment>
                            <div className="flex items-center box-border flex-row shrink-0 gap-m justify-start p-0 relative">
                                <Button
                                    aria-label="previous"
                                    disabled={is.undefined(onPrevious)}
                                    iconOnly={true}
                                    size="sm"
                                    variant="secondary"
                                    onClick={onPrevious}
                                >
                                    <IconArrowUp />
                                </Button>
                                <Button
                                    aria-label="next"
                                    disabled={is.undefined(onNext)}
                                    iconOnly={true}
                                    size="sm"
                                    variant="secondary"
                                    onClick={onNext}
                                >
                                    <IconArrowDown />
                                </Button>
                            </div>
                            <Seperator />
                        </Fragment>
                    )}
                    <LanguagePicker
                        activeText={activeLanguage}
                        disabled={isLanguagePickerDisabled}
                        isGenerating={isGenerating}
                        languages={languages}
                        onLanguageChange={setActiveLanguage}
                    />
                </NavigationButtons>
            </NavigationContainer>
            <ActionButtons>{actions}</ActionButtons>
        </HeaderContainer>
    );
};
